<template>
<div class="flex-body flex-container position-relative">
    <fast-table
        recordLabel="Pay Group Runs"
        tableClasses="table-anchor--id"
        :sections="sections"
        :headers="headers"
        :data="tableData"
        :has-row-select="true"
        :activeRowId="currentRun ? currentRun.id : null"
        @select="onSelect"
        @click="currentRun = $event"
    >
        <template v-slot:table-actions>
            <button type="button" class="btn btn-primary btn-collapse mx-2" :disabled="!paygroupRuns.length" @click.prevent="onCreateAll">
                <i class="far fa-fw fa-file-alt"></i> <span class="btn-text">Create All Files</span>
            </button>
        </template>
    </fast-table>

    <paygroup-sidebar
        v-if="currentRun"
        :client="sidebarClient"
        :run-id="currentRun.id"
        @close="currentRun = null"
    />

    <div v-if="selectedRuns.length" class="files-action-bar">
        <div class="files-action-bar-label">
            {{ selectedRuns.length }} {{ selectedRuns.length == 1 ? 'row' : 'rows' }} selected:
        </div>
        <div class="files-action-bar-buttons">
            <button type="button" class="btn btn-primary" @click.prevent="onCreateSelected">
                <i class="far fa-fw fa-file-alt"></i> Create Files
            </button>
        </div>
    </div>

    <create-files-confirm-modal
        v-if="createFilesModalOpen"
        :run-count="runsToCreateFor.length"
        :file-types="fileTypesToCreate"
        @close="onCreateCancelled"
        @create="onCreateConfirmed"
    />
</div>
</template>

<script>
import CreateFilesConfirmModal from '@/pages/admin/daily-payroll/components/CreateFilesConfirmModal'
import FastTable from '@/components/FastTable'
import PaygroupSidebar from '@/pages/client/payroll/components/PaygroupSidebar'

// PaygroupRun.<flag>_created names, in the column order the table shows them
const ALL_FILE_TYPES = ['verifund', 'asure', 'kotapay']

export default {
    props: {
        'paygroupRuns': {type: Array, required: true},
        // The tab this table is rendering: 'all', or the PaygroupRun flag behind one
        // tab ('verifund' = ACH, 'asure' = Taxes, 'kotapay' = OBC). A single-file-type
        // tab offers only that file type in the modal.
        'variant': {type: String, default: 'all'},
    },
    emits: ['updated', ],
    components: {CreateFilesConfirmModal, FastTable, PaygroupSidebar, },
    computed: {
        sidebarClient() {
            // PaygroupSidebar expects the payroll start screen's client object, but it
            // only uses client.id (for API calls) and client.companies (for names).
            if (!this.currentRun) {
                return null
            }
            const client = Object.assign({}, this.currentRun.company.client)
            client.companies = [this.currentRun.company]
            return client
        },
        selectedRuns() {
            return this.paygroupRuns.filter(run => this.selectedRunIds.includes(run.id))
        },
        runsToCreateFor() {
            // The modal covers either the current selection or, for Create All Files,
            // every row in the table
            return this.createAll ? this.paygroupRuns : this.selectedRuns
        },
        fileTypesToCreate() {
            // The All tab creates the whole set; a single-file-type tab creates only
            // its own file.
            if (this.variant == 'all') {
                return ALL_FILE_TYPES
            }
            return [this.variant]
        },
        tableData() {
            return [this.paygroupRuns.map(run => this.makeRow(run))]
        },
    },
    data() {
        return {
            selectedRunIds: [],
            currentRun: null,
            createFilesModalOpen: false,
            createAll: false,
            headers: [
                {label: 'Client ID', classes: 'cw-4', isSearchable: true},
                {label: 'Client', classes: 'cell-company', isSearchable: true, isFilterable: true},
                {label: 'Company', classes: 'cell-company', isSearchable: true, isFilterable: true},
                {label: 'Pay Group', classes: 'cw-8', isSearchable: true, isFilterable: true},
                {label: 'Run', classes: 'cw-3', isSearchable: false},
                {label: 'Check Date', classes: 'cw-date', type: 'date', isSearchable: false, defaultSort: true},
                {label: 'Pay Period', classes: 'cw-12', isSearchable: false},
                {label: 'Net Pay', classes: 'cw-pay cell-right', type: 'money', isSearchable: false, sortDataType: 'numeric'},
                // Explicit ids on both 'Taxes' columns: FastTable derives a column's id
                // from its label, so the money and checkmark ones would collide.
                {label: 'Taxes', classes: 'cw-pay cell-right', type: 'money', isSearchable: false, sortDataType: 'numeric', id: 'taxes-total'},
                {label: 'ACH', classes: 'cw-5 cell-center', type: 'checkmark', isSearchable: false, isFilterable: true, id: 'ach-created'},
                {label: 'Taxes', classes: 'cw-5 cell-center', type: 'checkmark', isSearchable: false, isFilterable: true, id: 'taxes-created'},
                {label: 'OBC', classes: 'cw-5 cell-center', type: 'checkmark', isSearchable: false, isFilterable: true, id: 'obc-created'},
            ],
            sections: [
                {title: 'Pay Group Runs', defaultIsClosed: false, id: 'runs'},
            ],
        }
    },
    watch: {
        paygroupRuns() {
            // Drop selections for rows that are no longer in the table
            const runIds = this.paygroupRuns.map(run => run.id)
            this.selectedRunIds = this.selectedRunIds.filter(runId => runIds.includes(runId))
        },
    },
    methods: {
        makeRow(run) {
            return {
                id: run.id,
                isSelected: this.selectedRunIds.includes(run.id),
                object: run,
                cells: [
                    run.company.client.client_code,
                    run.company.client.name,
                    run.company.name,
                    run.paygroup.name,
                    run.run_number,
                    run.check_date,
                    this.payPeriodLabel(run),
                    run.net_pay_total,
                    run.taxes_total,
                    run.verifund_created,
                    run.asure_created,
                    run.kotapay_created,
                ],
            }
        },
        payPeriodLabel(run) {
            if (!run.pay_period_start_date || !run.pay_period_end_date) {
                return ''
            }
            const startDate = this.$filters.date(run.pay_period_start_date, 'MM/DD/Y')
            const endDate = this.$filters.date(run.pay_period_end_date, 'MM/DD/Y')
            return `${startDate} - ${endDate}`
        },
        onSelect(rows) {
            this.selectedRunIds = rows.map(row => row.object.id)
        },
        onCreateSelected() {
            this.createAll = false
            this.createFilesModalOpen = true
        },
        onCreateAll() {
            this.createAll = true
            this.createFilesModalOpen = true
        },
        onCreateCancelled() {
            this.createFilesModalOpen = false
            this.createAll = false
        },
        onCreateConfirmed(fileTypes) {
            const paygroupRunIds = this.runsToCreateFor.map(run => run.id)
            this.createFilesModalOpen = false
            this.createAll = false
            // TODO: file creation is not implemented yet. Once it is, POST
            // paygroupRunIds + fileTypes, then emit 'updated' to refresh the table --
            // runs drop off as their *_created flags flip.
        },
    },
}
</script>

<style scoped>
.files-action-bar {
    position: absolute;
    bottom: 20px;
    left: 1rem;
    right: 1rem;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}
</style>
